import { breedGenome, type InheritanceReport } from '@/domain/genetics/breed';
import { randomGenome } from '@/domain/genetics/random';
import { typeSignature } from '@/domain/genetics/alchemy';
import { mkCreature } from '@/domain/creature/factory';
import { npcFromCreature, npcFromGenome } from '@/domain/npc/factory';
import { recordBirth } from '@/domain/lineage/mutate';
import { getEligiblePartners as getEligiblePartnersFromLineage } from '@/domain/lineage/eligibility';
import type { NpcCreature } from '@/domain/npc/types';
import type { Genome } from '@/domain/genetics/types';
import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import { uuid } from '@/infra/util/uuid';
import { saveCurrentSnapshot } from './save';
import { getWorldEvolutionService } from './world-evolution';
import './npc-events';

export interface BreedOutcome {
  ok: boolean;
  childId?: string;
  childName?: string;
  inheritance?: InheritanceReport;
  parentNames?: [string, string];
  reason?: 'no-active' | 'invalid-stage' | 'dead' | 'invalid-partner';
}

export interface BreedingService {
  canBreed(): boolean;
  getEligiblePartners(): NpcCreature[];
  breed(partnerId?: string): Promise<BreedOutcome>;
}

class BreedingServiceImpl implements BreedingService {
  canBreed(): boolean {
    const c = useStore.getState().creature;
    if (!c) return false;
    if (c.state.dead) return false;
    return c.lifecycle.stage === 'adult';
  }

  getEligiblePartners(): NpcCreature[] {
    const s = useStore.getState();
    if (!s.creature) return [];
    return getEligiblePartnersFromLineage(s.npcs, s.lineage, s.creature.id);
  }

  async breed(partnerId?: string): Promise<BreedOutcome> {
    const s = useStore.getState();
    const active = s.creature;
    if (!active) return { ok: false, reason: 'no-active' };
    if (active.state.dead) return { ok: false, reason: 'dead' };
    if (active.lifecycle.stage !== 'adult') {
      return { ok: false, reason: 'invalid-stage' };
    }

    const now = Date.now();

    let partnerGenome: Genome;
    let resolvedPartnerId: string;
    let partnerName: string;
    let partnerIsNew: boolean;
    let partnerCareScore: number;

    if (partnerId) {
      const chosen = getEligiblePartnersFromLineage(s.npcs, s.lineage, active.id).find(
        (n) => n.id === partnerId,
      );
      if (!chosen) return { ok: false, reason: 'invalid-partner' };
      partnerGenome = chosen.genome;
      resolvedPartnerId = chosen.id;
      partnerName = chosen.name;
      partnerIsNew = false;
      partnerCareScore = chosen.careFinalScore ?? 50;
    } else {
      partnerGenome = randomGenome();
      resolvedPartnerId = uuid();
      partnerName = `Parceiro de ${active.name}`;
      partnerIsNew = true;
      partnerCareScore = 50; // parceiro estranho: sem histórico.
    }

    eventBus.emit('breeding.started', { activeId: active.id });

    // Cruza
    const { genome: childGenome, inheritance } = breedGenome({
      parentA: active.genome,
      parentB: partnerGenome,
      parentACareScore: active.lifecycle.careFinalScore ?? 50,
      parentBCareScore: partnerCareScore,
    });
    const childId = uuid();
    const child = mkCreature({ genome: childGenome, id: childId, nowMs: now });

    // Atualiza lineage
    let lineage = s.lineage;
    if (!lineage.nodes[active.id]) {
      lineage = recordBirth(lineage, {
        id: active.id,
        name: active.name,
        genome: active.genome,
        traits: active.genome.inheritedTraits,
        bornAt: active.lifecycle.bornAt,
        parentIds: [null, null],
      });
    }
    // Active vira NPC sem perder a identidade familiar registrada.
    const activeNpc = npcFromCreature(active, now, lineage.nodes[active.id]?.parentIds);
    let npcs = s.npcs;
    if (partnerIsNew) {
      const partnerNpc = npcFromGenome({
        id: resolvedPartnerId,
        name: partnerName,
        bornAt: now - 30 * 60_000,
        retiredAt: now,
        parentIds: [null, null],
        genome: partnerGenome,
      });
      if (!lineage.nodes[resolvedPartnerId]) {
        lineage = recordBirth(lineage, {
          id: resolvedPartnerId,
          name: partnerNpc.name,
          genome: partnerGenome,
          traits: partnerGenome.inheritedTraits,
          bornAt: partnerNpc.bornAt,
          parentIds: [null, null],
        });
      }
      npcs = [...npcs, activeNpc, partnerNpc];
    } else {
      npcs = [...npcs, activeNpc];
    }
    lineage = recordBirth(lineage, {
      id: childId,
      name: child.name,
      genome: childGenome,
      traits: childGenome.inheritedTraits,
      bornAt: now,
      parentIds: [active.id, resolvedPartnerId],
    });

    // Atualiza store
    const sig = typeSignature(child.genome.primaryTypes);
    const discoveredTypes = s.discoveredTypes.includes(sig)
      ? s.discoveredTypes
      : [...s.discoveredTypes, sig];
    useStore.setState({
      creature: child,
      npcs,
      lineage,
      discoveredTypes,
    });

    eventBus.emit('breeding.completed', { childId, activeId: active.id, partnerId: resolvedPartnerId });
    logger.info('breeding.completed', { childId, parents: [active.id, resolvedPartnerId] });

    getWorldEvolutionService().onBreed(active.id, activeNpc.homeBiome ?? null);

    void saveCurrentSnapshot();
    return { ok: true, childId, childName: child.name, inheritance, parentNames: [active.name, partnerName] };
  }
}

let _instance: BreedingService | null = null;

export function getBreedingService(): BreedingService {
  if (!_instance) _instance = new BreedingServiceImpl();
  return _instance;
}

export function setBreedingServiceForTests(impl: BreedingService): void {
  _instance = impl;
}

export function resetBreedingServiceForTests(): void {
  _instance = null;
}
